import { z } from "zod";
import { matchedScheduleSchema } from "@common/schemas/domains/accounting/scheduleMatchedDisplay.schema";
import { scheduleMatchResolutionSchema } from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import { filtersSchema } from "@common/types/filters/general";
import {
    accountOverridesRequestSchema,
    accountOverridesResponseSchema,
} from "../accounting/account-override.schema";
import { classAssignmentSchema } from "../../class.schema";
import { counterpartySchema } from "../../counterparty.schema";

const lineItemAllocationSchema = z.object({
    lineItemId: z.string().uuid(),
    paymentAmount: z.number().positive(),
});

export type LineItemAllocation = z.infer<typeof lineItemAllocationSchema>;

const remainingAllocationSchema = z
    .object({
        amount: z.number().positive(),
        // Both shapes are accepted transitionally. `accountOverrides`
        // is canonical; `accrualAccountId` is the legacy single-id
        // form used by older clients + integration tests, resolved to
        // the entity's primary accrual ledger by the backend.
        accountOverrides: accountOverridesRequestSchema.optional(),
        accrualAccountId: z.string().uuid().optional(),
        classIds: z.array(z.string().uuid()).max(100).optional(),
        description: z.string().max(500).optional(),
        // Overrides the transaction's category on this remaining line.
        // Omitted = the line keeps the transaction's current category.
        categoryId: z.string().uuid().optional(),
    })
    .refine(
        (data) =>
            (data.accountOverrides?.length ?? 0) > 0 || !!data.accrualAccountId,
        {
            message:
                "Remaining allocation must include accountOverrides or accrualAccountId",
        },
    );

export type RemainingAllocation = z.infer<typeof remainingAllocationSchema>;

export const approveAccrualReviewBodySchema = z
    .object({
        transactionId: z.number().int(),
        // Both shapes are accepted transitionally. `accountOverrides`
        // is canonical; `accrualAccountId` resolves to the entity's
        // primary accrual ledger.
        accountOverrides: accountOverridesRequestSchema.optional(),
        accrualAccountId: z.string().uuid().optional(),
        matchedTransactionId: z.number().int().optional(),
        invoiceIds: z.array(z.string().uuid()).max(100).optional(),
        billIds: z.array(z.string().uuid()).max(100).optional(),
        lineItemAllocations: z
            .array(lineItemAllocationSchema)
            .max(100)
            .refine(
                (items) =>
                    new Set(items.map((i) => i.lineItemId)).size ===
                    items.length,
                { message: "Duplicate lineItemId in allocations" },
            )
            .optional(),
        remaining: z.array(remainingAllocationSchema).max(100).optional(),
        // When supplied, all audit logs sharing this sourceId are marked
        // `revertedAt = NOW()` after the action succeeds. Set by the History
        // drilldown drawer's "redo match" affordance to flag the
        // accrual-match-remove batch it just re-created.
        revertedSourceId: z.string().uuid().optional(),
        scheduleResolutions: z
            .array(scheduleMatchResolutionSchema)
            .max(100)
            .optional(),
    })
    .refine(
        (data) => {
            const hasDocuments =
                (data.invoiceIds?.length ?? 0) > 0 ||
                (data.billIds?.length ?? 0) > 0;
            const hasAllocations = (data.lineItemAllocations?.length ?? 0) > 0;
            const hasRemaining = (data.remaining?.length ?? 0) > 0;
            const hasAccount =
                (data.accountOverrides?.length ?? 0) > 0 ||
                !!data.accrualAccountId;
            const hasMatchedTransaction = !!data.matchedTransactionId;

            if (hasMatchedTransaction) {
                if (hasAccount) {
                    return false;
                }
                if (hasDocuments && hasAllocations) {
                    return false;
                }
                return true;
            }

            if (hasAccount) {
                return !hasDocuments && !hasAllocations && !hasRemaining;
            }

            if (hasDocuments) {
                return !hasAllocations && !hasRemaining;
            }

            return hasAllocations || hasRemaining;
        },
        {
            message:
                "Provide one of: accountOverrides, matchedTransactionId, invoiceIds/billIds, or lineItemAllocations and/or remaining",
        },
    );

export type ApproveAccrualReviewBody = z.infer<
    typeof approveAccrualReviewBodySchema
>;

// Caps are intentionally smaller than `bulkApprove`'s 500. This endpoint
// processes each tx through the full undo+approve service path
// (DB tx + distributed lock per step), so request duration scales
// linearly with N. A UI-driven selection is rarely more than ~20 txs;
// the 50/50 caps leave headroom while keeping the worst-case request
// bounded so a long sequential loop can't tie up backend resources or
// trip request timeouts.
export const REVERSE_APPROVE_TX_CAP = 50;

export const reverseApproveAccrualReviewBodyObjectSchema = z.object({
    transactionIds: z
        .array(z.number().int())
        .max(REVERSE_APPROVE_TX_CAP)
        .optional(),
    // Tx ids previously matched to this doc that should be detached.
    // Bundled with `transactionIds` so the client can express the full
    // diff in a single request. Each tx still goes through its own
    // locked DB transaction server-side; we intentionally avoid
    // wrapping the whole loop in an outer DB tx to prevent holding a
    // connection while inner steps wait on distributed locks. See
    // `reverseApprove` in the controller for the full rationale.
    removeTransactionIds: z
        .array(z.number().int())
        .max(REVERSE_APPROVE_TX_CAP)
        .optional(),
    invoiceId: z.string().uuid().optional(),
    billId: z.string().uuid().optional(),
    scheduleResolutions: z
        .array(scheduleMatchResolutionSchema)
        .max(100)
        .optional(),
});

export const reverseApproveAccrualReviewBodySchema =
    reverseApproveAccrualReviewBodyObjectSchema
        .refine((data) => Boolean(data.invoiceId) !== Boolean(data.billId), {
            message: "Provide exactly one of invoiceId or billId",
        })
        .refine(
            (data) =>
                (data.transactionIds?.length ?? 0) > 0 ||
                (data.removeTransactionIds?.length ?? 0) > 0,
            {
                message:
                    "Provide at least one of transactionIds or removeTransactionIds",
            },
        )
        .refine(
            (data) => {
                const adds = data.transactionIds ?? [];
                const removes = data.removeTransactionIds ?? [];
                const total = adds.length + removes.length;
                return new Set([...adds, ...removes]).size === total;
            },
            {
                message:
                    "Duplicate transactionId across transactionIds/removeTransactionIds",
            },
        );

export type ReverseApproveAccrualReviewBody = z.infer<
    typeof reverseApproveAccrualReviewBodySchema
>;

export const undoAccrualReviewBodySchema = z.object({
    transactionId: z.number().int(),
    // When supplied, all audit logs sharing this sourceId are marked
    // `revertedAt = NOW()` after the undo succeeds. Set by the History
    // drilldown drawer's "undo match" affordance to flag the
    // accrual-match-create batch it just removed.
    revertedSourceId: z.string().uuid().optional(),
});

export type UndoAccrualReviewBody = z.infer<typeof undoAccrualReviewBodySchema>;

const accrualMatchedDocumentSchema = z.object({
    id: z.string(),
    entityId: z.number().int().optional(),
    invoiceNumber: z.string().nullable(),
    total: z.number(),
    createdAt: z.coerce.date().optional(),
    counterpartyName: z.string().nullable().optional(),
});

const accrualMatchedTransactionSchema = z.object({
    id: z.number(),
    entityId: z.number().int().optional(),
    date: z.coerce.date(),
    amount: z.number(),
    displayDescription: z.string().nullable(),
    counterpartyName: z.string().nullable().optional(),
});

const accrualMatchLineItemBreakdownSchema = z.object({
    lineItemId: z.string().uuid(),
    amount: z.number(),
});

export type AccrualMatchLineItemBreakdown = z.infer<
    typeof accrualMatchLineItemBreakdownSchema
>;

export const accrualMatchDisplaySchema = z.object({
    invoice: accrualMatchedDocumentSchema.nullable().optional(),
    bill: accrualMatchedDocumentSchema.nullable().optional(),
    pairedTransaction: accrualMatchedTransactionSchema.nullable().optional(),
    amount: z.number(),
    lineItems: z.array(accrualMatchLineItemBreakdownSchema).optional(),
});

export type AccrualMatchDisplay = z.infer<typeof accrualMatchDisplaySchema>;

export const accrualMatchedTransactionDisplaySchema = z.object({
    transactionId: z.number().int(),
    date: z.coerce.date(),
    dateOnly: z.string().nullable().optional(),
    amount: z.number(),
    matchAmount: z.number(),
    displayDescription: z.string().nullable(),
    bankDescription: z.string(),
    counterparty: z.object({ name: z.string() }).nullable(),
    entity: z.object({
        id: z.number().int(),
        name: z.string().nullable(),
        iconColor: z.string(),
        workspaceId: z.string().uuid(),
        logo: z.string().nullable(),
    }),
    accrualAccountId: z.string().nullable(),
    financialAccountId: z.number().int().nullable(),
    splitParentId: z.number().int().nullable(),
    classAssignments: z.array(classAssignmentSchema),
});

export type AccrualMatchedTransactionDisplay = z.infer<
    typeof accrualMatchedTransactionDisplaySchema
>;

export const documentAccrualMatchesResponseSchema = z.object({
    matches: z.array(accrualMatchedTransactionDisplaySchema),
});

export const MAX_DOCUMENT_ACCRUAL_MATCH_SUMMARY_IDS = 100;

export const invoiceAccrualMatchSummariesQuerySchema = z.object({
    invoiceIds: z
        .array(z.string().uuid())
        .min(1)
        .max(MAX_DOCUMENT_ACCRUAL_MATCH_SUMMARY_IDS),
});

const documentAccrualMatchSummaryFields = {
    matchCount: z.number().int(),
    entities: z.array(accrualMatchedTransactionDisplaySchema.shape.entity),
};

export type DocumentAccrualMatchSummary = z.infer<
    z.ZodObject<typeof documentAccrualMatchSummaryFields>
>;

const invoiceAccrualMatchSummarySchema = z.object({
    invoiceId: z.string().uuid(),
    ...documentAccrualMatchSummaryFields,
});

export type InvoiceAccrualMatchSummary = z.infer<
    typeof invoiceAccrualMatchSummarySchema
>;

export const invoiceAccrualMatchSummariesResponseSchema = z.object({
    summaries: z.array(invoiceAccrualMatchSummarySchema),
});

export const billAccrualMatchSummariesQuerySchema = z.object({
    billIds: z
        .array(z.string().uuid())
        .min(1)
        .max(MAX_DOCUMENT_ACCRUAL_MATCH_SUMMARY_IDS),
});

const billAccrualMatchSummarySchema = z.object({
    billId: z.string().uuid(),
    ...documentAccrualMatchSummaryFields,
});

export type BillAccrualMatchSummary = z.infer<
    typeof billAccrualMatchSummarySchema
>;

export const billAccrualMatchSummariesResponseSchema = z.object({
    summaries: z.array(billAccrualMatchSummarySchema),
});

const accrualReviewTransactionSchema = z.object({
    id: z.number(),
    accrualAccountId: z.string().nullable(),
    accountOverrides: accountOverridesResponseSchema.optional(),
    amount: z.number(),
    splitParentId: z.number().nullable(),
    classAssignments: z.array(classAssignmentSchema),
    accrualMatches: z.array(accrualMatchDisplaySchema).optional(),
    counterpartyId: z.string().nullable(),
    matchedSchedules: z.array(matchedScheduleSchema).optional(),
    counterparty: counterpartySchema
        .pick({
            id: true,
            name: true,
            shouldBeNormalized: true,
            updatedByUserId: true,
            source: true,
            workspaceId: true,
        })
        .nullable()
        .optional(),
});

export const accrualReviewApproveResponseSchema = z.object({
    updatedTransactions: z.array(accrualReviewTransactionSchema),
    createdSplits: z.array(accrualReviewTransactionSchema),
});

export type AccrualReviewApproveResponse = z.infer<
    typeof accrualReviewApproveResponseSchema
>;

export const accrualReviewUndoResponseSchema = z.object({
    updatedTransactions: z.array(accrualReviewTransactionSchema),
    deletedSplitIds: z.array(z.number()),
});

export type AccrualReviewUndoResponse = z.infer<
    typeof accrualReviewUndoResponseSchema
>;

export const accrualReviewWorkspacePathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
});

// Same bound as `REVERSE_APPROVE_TX_CAP`: each id runs the full undo path
// (DB tx + distributed lock + journal entry regeneration) in sequence, so the
// client sends larger selections in chunks of this size.
export const BULK_UNMATCH_INVOICES_TX_CAP = 50;

export const bulkUnmatchInvoicesBodySchema = z.object({
    transactionIds: z
        .array(z.number().int())
        .min(1)
        .max(BULK_UNMATCH_INVOICES_TX_CAP)
        .refine((ids) => new Set(ids).size === ids.length, {
            message: "transactionIds must be unique",
        }),
});

export type BulkUnmatchInvoicesBody = z.infer<
    typeof bulkUnmatchInvoicesBodySchema
>;

export const bulkUnmatchInvoicesResponseSchema = z.object({
    unmatchedCount: z.number().int(),
    /**
     * Selected transactions left untouched: no invoice match, a split child
     * covered by its selected parent's undo, matches already gone by the time
     * its undo ran, or a transfer match (also counted in
     * `transferMatchSkippedCount`).
     */
    skippedCount: z.number().int(),
    /**
     * Invoice-matched transactions that also carry a transfer match. Undoing
     * them would drop the transfer pairing too, so they're left for the
     * single-transaction undo.
     */
    transferMatchSkippedCount: z.number().int(),
    failedCount: z.number().int(),
    failedTransactionIds: z.array(z.number().int()),
});

export type BulkUnmatchInvoicesResponse = z.infer<
    typeof bulkUnmatchInvoicesResponseSchema
>;

export const bulkApproveAccrualReviewBodySchema = z
    .object({
        transactionIds: z.array(z.number().int()).min(1).max(500).optional(),
        filters: filtersSchema.optional(),
        /**
         * Optional pagination applied only when `filters` is provided. When
         * set, the server loads exactly one page of matching transactions
         * (skipping the legacy 500-cap loop) so the client can drain a large
         * result set across multiple requests. Omitting both keeps the
         * backwards-compatible "up to 500 matching" behavior.
         */
        page: z.number().int().nonnegative().optional(),
        limit: z.number().int().positive().max(200).optional(),
        accountOverrides: z
            .record(
                z.string().regex(/^\d+$/),
                z.union([z.string().uuid(), accountOverridesRequestSchema]),
            )
            .refine((r) => Object.keys(r).length <= 500, {
                message: "accountOverrides cannot exceed 500 entries",
            })
            .optional(),
    })
    .refine((data) => Boolean(data.transactionIds) !== Boolean(data.filters), {
        message: "Provide either transactionIds or filters, not both",
    })
    .refine(
        (data) =>
            (data.page === undefined && data.limit === undefined) ||
            Boolean(data.filters),
        {
            message: "page/limit are only valid when filters is provided",
        },
    );

export type BulkApproveAccrualReviewBody = z.infer<
    typeof bulkApproveAccrualReviewBodySchema
>;

export const bulkApproveAccrualReviewResponseSchema = z.object({
    approvedCount: z.number(),
    skippedReviewCount: z.number(),
    skippedScheduleConflictCount: z.number(),
    skippedAlreadyAcceptedCount: z.number(),
    failedCount: z.number(),
    updatedTransactions: z.array(accrualReviewTransactionSchema),
    createdSplits: z.array(accrualReviewTransactionSchema),
});

export type BulkApproveAccrualReviewResponse = z.infer<
    typeof bulkApproveAccrualReviewResponseSchema
>;

// Cheap existence probe for the transaction-match drawer: it decides whether
// to offer the Invoice/Bill tabs at all before the user has picked a search
// type, so it has to be safe to call on every drawer open without the cost of
// a real list query (joins, pagination, filters). Combined into one endpoint
// because the drawer always needs both flags together to build its tab set.
export const hasMatchableDocumentsResponseSchema = z.object({
    hasInvoices: z.boolean(),
    hasBills: z.boolean(),
});

export type HasMatchableDocumentsResponse = z.infer<
    typeof hasMatchableDocumentsResponseSchema
>;
